import { createSlice } from "@reduxjs/toolkit";
import {
  getClaimantsByCaseIDAction,
  createClaimantDetailsAction,
  updateClaimantDetailsAction,
  deleteClaimantDetailsAction,
  getClaimantDetailsAction,
  uploadClaimantDocumentAction,
  updateClaimantDocumentAction,
  deleteClaimantDocumentAction,
  getClaimantDocumentsAction,
} from "../../actions/ClaimantDetails/claimantDetailsAction";

// ============================================================================
// INITIAL STATE
// ============================================================================

const initialState = {
  claimants: [],
  claimantDetails: null,
  documents: [],
  loading: false,
  error: null,
  success: false,
  successMessage: "",
};

// ============================================================================
// SLICE
// ============================================================================

const claimantDetailsSlice = createSlice({
  name: "claimantDetails",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = false;
      state.successMessage = "";
    },
    resetClaimantDetails: (state) => {
      state.claimantDetails = null;
    },
  },
  extraReducers: (builder) => {
    // Create Claimant Details
    builder
      .addCase(createClaimantDetailsAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createClaimantDetailsAction.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.successMessage = "Claimant created successfully";
        state.claimants.push(action.payload.data);
      })
      .addCase(createClaimantDetailsAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Update Claimant Details
    builder
      .addCase(updateClaimantDetailsAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateClaimantDetailsAction.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.successMessage = "Claimant updated successfully";
        const index = state.claimants.findIndex(
          (c) => c.claimantDetailsID === action.payload.data.claimantDetailsID,
        );
        if (index !== -1) {
          state.claimants[index] = action.payload.data;
        }
      })
      .addCase(updateClaimantDetailsAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Delete Claimant Details
    builder
      .addCase(deleteClaimantDetailsAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteClaimantDetailsAction.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.successMessage = "Claimant deleted successfully";
        state.claimants = state.claimants.filter(
          (c) => c.claimantDetailsID !== action.payload,
        );
      })
      .addCase(deleteClaimantDetailsAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Get Claimant Details
    builder
      .addCase(getClaimantDetailsAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getClaimantDetailsAction.fulfilled, (state, action) => {
        state.loading = false;
        state.claimantDetails = action.payload.data;
      })
      .addCase(getClaimantDetailsAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Get All Claimants for a Case
    builder
      .addCase(getClaimantsByCaseIDAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getClaimantsByCaseIDAction.fulfilled, (state, action) => {
        state.loading = false;
        state.claimants = action.payload.data || [];
      })
      .addCase(getClaimantsByCaseIDAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Upload Document
    builder
      .addCase(uploadClaimantDocumentAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(uploadClaimantDocumentAction.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.successMessage = "Document uploaded successfully";
        state.documents = [
          ...state.documents,
          ...(Array.isArray(action.payload.data)
            ? action.payload.data
            : [action.payload.data]),
        ];
      })
      .addCase(uploadClaimantDocumentAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Update Document
    builder
      .addCase(updateClaimantDocumentAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateClaimantDocumentAction.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.successMessage = "Document updated successfully";
        const index = state.documents.findIndex(
          (d) => d.documentID === action.payload.data.documentID,
        );
        if (index !== -1) {
          state.documents[index] = action.payload.data;
        }
      })
      .addCase(updateClaimantDocumentAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Delete Document
    builder
      .addCase(deleteClaimantDocumentAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteClaimantDocumentAction.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.successMessage = "Document deleted successfully";
        state.documents = state.documents.filter(
          (d) => d.documentID !== action.payload,
        );
      })
      .addCase(deleteClaimantDocumentAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Get Documents
    builder
      .addCase(getClaimantDocumentsAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getClaimantDocumentsAction.fulfilled, (state, action) => {
        state.loading = false;
        state.documents = action.payload.data || [];
      })
      .addCase(getClaimantDocumentsAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, clearSuccess, resetClaimantDetails } =
  claimantDetailsSlice.actions;
export default claimantDetailsSlice.reducer;
